/**
 * Outil « Relation de Chasles » (Boîte à outils, 2nde → 1re).
 *
 * Trois points A, B, C sur le quadrillage. Mode « Somme » : AB et BC mis
 * bout à bout donnent AC, coordonnées additionnées sous les yeux. Mode
 * « Colinéarité » : le déterminant de AB et AC, nul quand A, B, C sont
 * alignés. Défis : placer C, aligner, faire de B un milieu.
 */

import { useRef, useState } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import type { ToolProps } from '../../tool-types';
import { Note, PRIMARY_BUTTON, Segmented, ToggleChip } from '../../ui/controls';
import { makeFrame, snapPoint, type Pt } from '../../ui/frame';
import { formatCoords } from '../../ui/format';
import { DragHandle, FIGURE_BG_CLASS, INK, PlotGrid, VectorArrow } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import { add, det, GOALS, texColumn, texFactor, texSum, vec, type Pts } from './vectors';

const SIZE = 320;
const VP = { xMin: -6, xMax: 6, yMin: -6, yMax: 6 };
const frame = makeFrame(VP, SIZE);
const AB_COLOR = 'var(--color-primary-500)';
const BC_COLOR = 'var(--color-accent-500)';
const AC_COLOR = 'var(--color-xp-500)';
const START: Pts = { A: { x: -4, y: -2 }, B: { x: 0, y: -1 }, C: { x: 1, y: 3 } };
type Name = keyof Pts;
const NAMES: readonly Name[] = ['A', 'B', 'C'];

const ov = (n: string) => `\\overrightarrow{${n}}`;

export function VectorSumTool(_props: ToolProps) {
  const [pts, setPts] = useState<Pts>(START);
  const [mode, setMode] = useState<'sum' | 'collinear'>('sum');
  const [goal, setGoal] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const drag = useDrag<Name>({
    svgRef,
    frame,
    ids: NAMES,
    pointOf: (n) => pts[n],
    onDrag: (n, p) =>
      setPts((old) => {
        const q = snapPoint(p, VP, 1, 0.5);
        const taken = NAMES.some((m) => m !== n && old[m].x === q.x && old[m].y === q.y);
        return taken ? old : { ...old, [n]: q };
      }),
  });

  const ab = vec(pts.A, pts.B);
  const bc = vec(pts.B, pts.C);
  const ac = vec(pts.A, pts.C);
  const sum = add(ab, bc);
  const d = det(ab, ac);
  const px = (p: Pt) => ({ x: frame.tx(p.x), y: frame.ty(p.y) });
  const [pa, pb, pc] = [px(pts.A), px(pts.B), px(pts.C)] as const;
  const goalDone = goal !== null && GOALS[goal]!.check(pts);

  const rows =
    mode === 'sum'
      ? [
          { name: 'AB', v: ab, color: AB_COLOR },
          { name: 'BC', v: bc, color: BC_COLOR },
          { name: 'AC', v: ac, color: AC_COLOR },
        ]
      : [
          { name: 'AB', v: ab, color: AB_COLOR },
          { name: 'AC', v: ac, color: AC_COLOR },
        ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Mode"
          value={mode}
          options={[
            { value: 'sum', label: 'Somme' },
            { value: 'collinear', label: 'Colinéarité' },
          ]}
          onChange={setMode}
        />
        <ToggleChip on={goal !== null} onClick={() => setGoal((g) => (g === null ? 0 : null))}>
          Défi
        </ToggleChip>
      </div>

      {goal !== null && (
        <section
          aria-live="polite"
          className="bg-xp-50 space-y-2 rounded-2xl p-3"
          data-testid="vector-goal"
        >
          <p className="text-ink-900 text-sm font-semibold">
            Défi {goal + 1}/{GOALS.length} · {GOALS[goal]!.text}
          </p>
          {goalDone ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-success-600 text-sm font-bold">Réussi !</p>
              {goal + 1 < GOALS.length && (
                <button
                  type="button"
                  onClick={() => setGoal(goal + 1)}
                  className={cn(PRIMARY_BUTTON, 'ml-auto')}
                >
                  Défi suivant
                </button>
              )}
            </div>
          ) : (
            <p className="text-ink-600 text-sm">Déplace les points A, B ou C.</p>
          )}
        </section>
      )}

      <p className="text-ink-700 text-sm" data-testid="vector-points">
        A{formatCoords(pts.A.x, pts.A.y)} · B{formatCoords(pts.B.x, pts.B.y)} · C
        {formatCoords(pts.C.x, pts.C.y)}
      </p>

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label={
            mode === 'sum'
              ? 'Les vecteurs AB et BC bout à bout, et leur somme AC'
              : 'Les vecteurs AB et AC partant de A'
          }
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} step={1} />
          {mode === 'collinear' && d === 0 && (
            <line
              x1={frame.tx(pts.A.x - 20 * ac.x)}
              y1={frame.ty(pts.A.y - 20 * ac.y)}
              x2={frame.tx(pts.A.x + 20 * ac.x)}
              y2={frame.ty(pts.A.y + 20 * ac.y)}
              stroke={AC_COLOR}
              strokeWidth={1.5}
              strokeDasharray="6 5"
            />
          )}
          <VectorArrow
            x1={pa.x}
            y1={pa.y}
            x2={pc.x}
            y2={pc.y}
            color={AC_COLOR}
            width={3.5}
            inset={8}
          />
          <VectorArrow x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} color={AB_COLOR} inset={8} />
          {mode === 'sum' && (
            <VectorArrow x1={pb.x} y1={pb.y} x2={pc.x} y2={pc.y} color={BC_COLOR} inset={8} />
          )}
          {NAMES.map((n) => {
            const p = px(pts[n]);
            return (
              <DragHandle
                key={n}
                x={p.x}
                y={p.y}
                color={INK}
                label={n}
                labelDx={-14}
                labelDy={-14}
                name={`Point ${n} ${formatCoords(pts[n].x, pts[n].y)}`}
                active={drag.active === n}
                keyProps={drag.keyProps(n)}
              />
            );
          })}
        </svg>
      </div>

      <section aria-label="Coordonnées des vecteurs" className="flex flex-wrap gap-2">
        {rows.map((r) => (
          <div
            key={r.name}
            className="border-ink-100 flex items-center gap-2 rounded-2xl border px-3 py-1.5"
          >
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ background: r.color }}
              aria-hidden
            />
            <MathText className="text-ink-900">{`$${ov(r.name)}${texColumn(r.v.x, r.v.y)}$`}</MathText>
          </div>
        ))}
      </section>

      {mode === 'sum' ? (
        <Note tone="success">
          <span className="block overflow-x-auto" data-testid="vector-sum">
            <MathText>{`$${ov('AB')} + ${ov('BC')} = ${texColumn(
              texSum(ab.x, bc.x),
              texSum(ab.y, bc.y),
            )} = ${texColumn(sum.x, sum.y)} = ${ov('AC')}$`}</MathText>
          </span>
          <span className="mt-1 block">
            Relation de Chasles : l’extrémité de AB est l’origine de BC, la somme va de A à C.
          </span>
        </Note>
      ) : (
        <Note tone={d === 0 ? 'success' : 'error'}>
          <span className="block overflow-x-auto" data-testid="vector-det">
            <MathText>{`$\\det(${ov('AB')}, ${ov('AC')}) = ${texFactor(ab.x)} \\times ${texFactor(
              ac.y,
            )} - ${texFactor(ac.x)} \\times ${texFactor(ab.y)} = ${d}$`}</MathText>
          </span>
          <span className="mt-1 block">
            {d === 0
              ? 'Le déterminant est nul : les vecteurs sont colinéaires, donc A, B et C sont alignés.'
              : 'Le déterminant n’est pas nul : les vecteurs ne sont pas colinéaires, A, B et C ne sont pas alignés.'}
          </span>
        </Note>
      )}
    </div>
  );
}
